import {useState, useMemo} from 'react';
import {Button, Chip, Typography} from '@mui/material';
import {styled} from '@mui/material/styles';
import Collapse from '@mui/material/Collapse';
import Skeleton from '@mui/material/Skeleton';
import {random} from 'lodash/fp';

import {ActiveFilterSectionContent, ActiveFilterChip} from 'filters/features/FilterDialog/types';
import {Falsy} from 'types';

import {useActiveFiltersContext} from 'filters/features/FilterDialog/sections/ActiveFiltersSection/contexts/ActiveFiltersContext';
import {DISABLED_FILTERS_MESSAGE} from 'filters/features/FilterDialog/constants';

const COLLAPSIBLE_ITEMS_LIMIT = 10;

const SList = styled('div')(({theme}) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
}));

const SCollapsibleList = styled('div')(({theme}) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

const SButton = styled(Button)(({theme}) => ({
  marginTop: theme.spacing(2),
}));

const FilterChip = ({disabled, label, onClick, onDelete}: ActiveFilterChip) => {
  const {loading} = useActiveFiltersContext();

  if (loading) {
    return <Skeleton variant='rounded' height={32} width={random(80, 180)} />;
  }

  return (
    <Chip
      disabled={disabled}
      onClick={onClick}
      onDelete={disabled ? undefined : onDelete}
      label={label}
      color='primary'
    />
  );
};

const FilterChips = ({chips}: {chips: Falsy<ActiveFilterChip>[]}) => {
  const {loading} = useActiveFiltersContext();

  const [showMore, setShowMore] = useState(false);
  const filtered = chips.filter(Boolean);

  if (!filtered) {
    return null;
  }

  return (
    <>
      <SList>
        {filtered?.slice(0, COLLAPSIBLE_ITEMS_LIMIT)?.map(chip => (
          <FilterChip {...chip} key={chip.id} />
        ))}
      </SList>

      {filtered.length > COLLAPSIBLE_ITEMS_LIMIT && (
        <Collapse in={showMore}>
          <SCollapsibleList>
            {filtered?.slice(COLLAPSIBLE_ITEMS_LIMIT).map(chip => (
              <FilterChip {...chip} key={chip.id} />
            ))}
          </SCollapsibleList>
        </Collapse>
      )}

      {filtered.length > COLLAPSIBLE_ITEMS_LIMIT && (
        <SButton variant='text' size='small' disabled={loading} onClick={() => setShowMore(prev => !prev)}>
          {showMore ? 'Show Less' : 'Show More'}
        </SButton>
      )}
    </>
  );
};

const SContent = styled('div')(({theme}) => ({
  '&:not(:last-child)': {
    marginBottom: theme.spacing(4),
  },
}));

const fallback = (content: ActiveFilterSectionContent) => content;

export const ActiveFiltersContent = ({content}: {content: ActiveFilterSectionContent | null}) => {
  const {overrideContent} = useActiveFiltersContext();
  const filtered = useMemo(() => {
    return content?.filter(Boolean);
  }, [content]);

  if (!filtered?.some(content => content.chips?.length > 0)) {
    return null;
  }

  return (overrideContent || fallback)(filtered)
    .filter(Boolean)
    .map(({chips, disabled, label}, index) => (
      <SContent key={index}>
        {label && (
          <Typography variant='subtitle2' color='text.secondary' marginBottom={2}>
            {label}
          </Typography>
        )}
        {disabled && (
          <Typography variant='subtitle3' color='text.secondary' paragraph marginBottom={2}>
            {DISABLED_FILTERS_MESSAGE}
          </Typography>
        )}
        <FilterChips chips={chips} />
      </SContent>
    ));
};
